export class AnimationController {
  constructor(config = {}) {
    this.config = {
      animationDuration: 300,
      animationEasing: 'ease',
      showClass: 'pixl-search-show',
      hideClass: 'pixl-search-hide',
      enabled: true,
      highlightColor: '#ffeb3b',
      highlightClass: 'pixl-highlight',
      ...config
    };
    
    this.styleSheet = null;
    this.injectStyles();
  }

  injectStyles() {
    if (this.styleSheet) return;
    
    const style = document.createElement('style');
    style.textContent = `
      .pixl-search-hide {
        animation: pixl-search-fade-out ${this.config.animationDuration}ms ${this.config.animationEasing} forwards;
      }
      
      .pixl-search-show {
        animation: pixl-search-fade-in ${this.config.animationDuration}ms ${this.config.animationEasing} forwards;
      }
      
      .pixl-search-hidden {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }
      
      @keyframes pixl-search-fade-out {
        from {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }
      
      @keyframes pixl-search-fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      
      .${this.config.highlightClass} {
        background-color: ${this.config.highlightColor};
        padding: 0 2px;
        border-radius: 2px;
        font-weight: 500;
      }
      
      [pixl-search-field] {
        transition: border-color 0.2s ease;
      }
      
      [pixl-search-field]:focus {
        outline: none;
        border-color: #4CAF50;
      }
      
      .pixl-search-loading {
        position: relative;
        pointer-events: none;
      }
      
      .pixl-search-loading::after {
        content: '';
        position: absolute;
        top: 50%;
        right: 10px;
        width: 16px;
        height: 16px;
        margin-top: -8px;
        border: 2px solid #ddd;
        border-top-color: #333;
        border-radius: 50%;
        animation: pixl-search-spin 0.6s linear infinite;
      }
      
      @keyframes pixl-search-spin {
        to {
          transform: rotate(360deg);
        }
      }
      
      /* Default styling for generated empty states only */
      .pixl-search-empty-state[data-pixl-generated] {
        text-align: left;
        padding: 40px 20px;
        color: #666666;
        font-size: 16px;
        font-weight: normal;
        display: none;
      }
      
      .pixl-search-empty-state[data-pixl-generated].show {
        display: block;
      }
    `;
    
    document.head.appendChild(style);
    this.styleSheet = style;
  }

  showItem(item) {
    if (!this.config.enabled) {
      item.classList.remove('pixl-search-hidden');
      item.style.opacity = '';
      item.style.visibility = '';
      return Promise.resolve();
    }

    return new Promise(resolve => {
      item.classList.remove('pixl-search-hide', 'pixl-search-hidden');
      item.style.opacity = '';
      item.style.visibility = '';
      
      requestAnimationFrame(() => {
        item.classList.add('pixl-search-show');
        
        setTimeout(() => {
          item.classList.remove('pixl-search-show');
          resolve();
        }, this.config.animationDuration);
      });
    });
  }

  hideItem(item) {
    if (!this.config.enabled) {
      item.classList.add('pixl-search-hidden');
      return Promise.resolve();
    }

    return new Promise(resolve => {
      item.classList.remove('pixl-search-show');
      item.classList.add('pixl-search-hide');
      
      setTimeout(() => {
        item.classList.add('pixl-search-hidden');
        item.classList.remove('pixl-search-hide');
        resolve();
      }, this.config.animationDuration);
    });
  }

  showItems(items) {
    if (!this.config.enabled) {
      items.forEach(item => {
        item.classList.remove('pixl-search-hidden');
        item.style.opacity = '';
        item.style.visibility = '';
      });
      return Promise.resolve();
    }

    const promises = items.map((item, index) => {
      return new Promise(resolve => {
        setTimeout(() => {
          this.showItem(item).then(resolve);
        }, index * 50);
      });
    });
    
    return Promise.all(promises);
  }

  hideItems(items) {
    if (!this.config.enabled) {
      items.forEach(item => {
        item.classList.add('pixl-search-hidden');
      });
      return Promise.resolve();
    }

    const promises = items.map(item => this.hideItem(item));
    return Promise.all(promises);
  }

  showLoadingState(element) {
    element.classList.add('pixl-search-loading');
  }

  hideLoadingState(element) {
    element.classList.remove('pixl-search-loading');
  }

  showEmptyState(container, message = 'No results found in this category', customStyles = {}, targetClass = '') {
    console.log('=== showEmptyState called ===');
    console.log('Container:', container);
    console.log('Target class:', targetClass);
    
    // Simple approach: if target class is provided, show those elements
    if (targetClass) {
      // Only select original elements, not cloned ones
      const targetElements = document.querySelectorAll(`.${targetClass}:not([data-pixl-cloned])`);
      console.log(`Found ${targetElements.length} original elements with class "${targetClass}"`);
      
      targetElements.forEach((element, index) => {
        console.log(`Element ${index + 1}:`, element);
        console.log('Parent:', element.parentElement);
        console.log('Is inside container?', container.contains(element));
        
        // Check if element is inside the collection list container
        if (container.contains(element)) {
          console.log('✅ Element is inside collection list - showing it');
          element.style.display = 'block';
        } else {
          console.log('❌ Element is OUTSIDE collection list - need to move or clone it');
          // Check if a clone already exists in this container
          const existingClone = container.querySelector(`[data-pixl-cloned="true"]`);
          if (!existingClone) {
            // Only create clone if none exists
            const clone = element.cloneNode(true);
            clone.setAttribute('data-pixl-cloned', 'true');
            container.appendChild(clone);
            clone.style.display = 'block';
            console.log('✅ Created new clone in container');
          } else {
            // Clone already exists, just show it
            existingClone.style.display = 'block';
            console.log('✅ Showing existing clone in container');
          }
          // Hide the original
          element.style.display = 'none';
        }
      });
      return;
    }
    
    console.log('No target class provided, using fallback');
    // Fallback: create generated empty state
    let emptyState = container.querySelector('.pixl-search-empty-state[data-pixl-generated]');
    
    if (!emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'pixl-search-empty-state';
      emptyState.setAttribute('data-pixl-generated', 'true');
      container.appendChild(emptyState);
    }
    
    // Apply custom styling if provided
    if (customStyles.color) {
      emptyState.style.color = customStyles.color;
    }
    if (customStyles.fontSize) {
      emptyState.style.fontSize = customStyles.fontSize;
    }
    if (customStyles.padding) {
      emptyState.style.padding = customStyles.padding;
    }
    if (customStyles.textAlign) {
      emptyState.style.textAlign = customStyles.textAlign;
    }
    if (customStyles.fontWeight) {
      emptyState.style.fontWeight = customStyles.fontWeight;
    }
    
    emptyState.textContent = message;
    emptyState.classList.add('show');
  }

  hideEmptyState(container, targetClass = '') {
    // Simple: if target class is provided, hide those elements
    if (targetClass) {
      // Hide original elements
      const targetElements = document.querySelectorAll(`.${targetClass}`);
      targetElements.forEach(element => {
        element.style.display = 'none';
      });
      
      // Remove any cloned elements
      const clonedElements = container.querySelectorAll(`[data-pixl-cloned="true"]`);
      clonedElements.forEach(element => {
        element.remove();
      });
      return;
    }
    
    // Hide generated empty states
    const generatedEmptyStates = container.querySelectorAll('.pixl-search-empty-state[data-pixl-generated]');
    generatedEmptyStates.forEach(emptyState => {
      emptyState.classList.remove('show');
    });
  }

  updateAnimationDuration(duration) {
    this.config.animationDuration = duration;
    this.styleSheet.remove();
    this.styleSheet = null;
    this.injectStyles();
  }

  updateHighlightColor(color) {
    this.config.highlightColor = color;
    this.styleSheet.remove();
    this.styleSheet = null;
    this.injectStyles();
  }

  updateHighlightClass(className) {
    this.config.highlightClass = className;
    this.styleSheet.remove();
    this.styleSheet = null;
    this.injectStyles();
  }

  enable() {
    this.config.enabled = true;
  }

  disable() {
    this.config.enabled = false;
  }
}